import React from "react";
import { useNavigate } from "react-router-dom";
import { restoreSession, signOut } from "services/auth";
import { Box, CircularProgress } from "@mui/material";

import { useServices } from "contexts/SingleSessionProvider";

import useNavigateWithClient from "hooks/useNavigateWithClient";

import Session from "utils/Session";
import { subMinutes } from "utils";

function SessionHelper({ children }) {
  const navigate = useNavigate();
  const service = useServices();
  const navigateWithClient = useNavigateWithClient();

  const [loading, setLoading] = React.useState(true);

  const isTokenExpiring = () => {
    console.log("DEBUG::isTokenExpiring");
    return subMinutes(new Date(Session.accessTokenExpiry), 5) <= new Date();
  };
  const isRefreshTokenExpiring = () => {
    return (
      subMinutes(new Date(Session.refreshTokenExpiry), 5).getTime() <=
      new Date().getTime()
    );
  };

  const goToSignin = async () => {
    console.log("DEBUG::gotToSignin");
    await signOut();
    navigateWithClient("/login");
  };

  async function onLoad() {
    setLoading(true);

    // NOTE: mobile users are no longer redirected to /unsupported-device.
    // The root router (src/screens/index.jsx) branches on isMobile() and
    // renders the mobile app (src/mobile) instead. This component still owns
    // the shared auth/session bootstrap below for both desktop and mobile.

    try {
      // if the refresh token is expiring then sign out and redirect to sign in
      const languifyRefreshTokenExpiry = localStorage?.getItem(
        "languifyRefreshTokenExpiry"
      );

      if (languifyRefreshTokenExpiry) {
        const refreshTokenExpiryTime = new Date(
          parseInt(languifyRefreshTokenExpiry, 10)
        ).getTime();
        Session.refreshTokenExpiry =
          refreshTokenExpiryTime <= new Date().getTime()
            ? new Date().getTime()
            : refreshTokenExpiryTime;
      }

      if (isRefreshTokenExpiring()) {
        console.log("DEBUG::RefreshTokenExpired");
        if (Session.isLoggedIn()) await goToSignin();
        return;
      }

      if (Session.isLoggedIn()) {
        console.log("DEBUG::isLoggedIn: ", Session.isLoggedIn());
        if (isTokenExpiring()) {
          console.log("DEBUG::AccessTokenExpired");
          Session.accessToken = "";
        }

        await service.callService(restoreSession);

        const deactivated = Session.checkDeactivatedStatus();

        if (deactivated) {
          console.log("DEBUG::DEACTIVATED");

          await signOut();

          navigate("/error-admin");
        }
        setLoading(false);
      }
    } catch (e) {
      console.log("DEBUG::onLoad error: ", e);
      goToSignin();
    } finally {
      setLoading(false);
    }
  }

  async function setupRefresh() {
    try {
      if (isRefreshTokenExpiring()) {
        console.log("DEBUG::RefreshTokenExpired-2");
        await goToSignin();
      }

      if (Session.isLoggedIn()) {
        if (isTokenExpiring()) Session.refreshToken = "";

        await service.callService(restoreSession);
      }
    } catch (e) {
      console.error("setupTokenRefresh -> error refreshing token", e);
      await goToSignin();
    }
  }

  React.useEffect(() => {
    onLoad()
      .then(() => setLoading(false))
      .catch((e) => setLoading(false));

    const timerId = setInterval(setupRefresh, 3600000);

    return () => clearInterval(timerId);
  }, []);

  return loading ? (
    <Box
      width={"100vw"}
      height={"100vh"}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress variant="indeterminate" />
    </Box>
  ) : (
    <>{children}</>
  );
}

export default SessionHelper;
